import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "../../../components/forms";
import DocumentUpload from "./DocumentUpload";
import { toast } from "react-toastify";
import parseError from "../../../utils/errorUtils";
import { uploadFile } from "../../../services/fileService";
import LoadingSpinner from '../../../components/loadingSpinner/LoadingSpinner';
import IDUploadImagePopup from "./IDUploadImagePopup";
import ImageUploadGuidelinesPopup from "./ImageUploadGuidelinesPopup";
import { useAuth } from "../../../utils/authUtils";

const IdentificationForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const auth = useAuth();
  const [numberOfDocuments, setNumberOfDocuments] = useState(0)

  const toggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const handleNavigate = () =>
    navigate("/onboarding/eligibility-flow/kyc-document");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const eligibilityForm = JSON.parse(localStorage.getItem("EligibilityForm") || "{}");
  const profileData = {};

  useEffect(() => {
    const storedFirstName = profileData?.firstName || eligibilityForm?.["First name"];
    const storedLastName = profileData?.lastName || eligibilityForm?.["Last name"];

    setFirstName(storedFirstName || "");
    setLastName(storedLastName || "");

    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    setIsMobile(isMobile);
  }, [eligibilityForm, profileData]);

  const handleSubmit = async (values) => {

    try {
      const updatedDocuments = values?.documents?.map((doc) => ({
        file: doc,
        type: 'PATIENT_PROFILE_PICTURE',
      }));
      let res;

      var formData = new FormData();

      formData.append("action", "PATIENT_KYC_DOCUMENTS");

      for (const fileData of updatedDocuments ?? []) {
        formData.append(
          "files",
          fileData.file,
          `${fileData.type}.${fileData.file.name.split(".")[1]}`
        );
        formData.append("category", fileData.type);

        setIsSubmitting(true);
        res = await uploadFile(formData);
        formData.delete("files");
        formData.delete("category");
      }

      if (res?.error) {
        setIsSubmitting(false);
        toast.error(parseError(res.error, "Couldn't upload file"));
      } else {
        setIsSubmitting(false);
        (Object.keys(values).length !== 0 && !isMobile) && toast.success("Successfully saved");
        Object.keys(values).length !== 0 && handleNavigate();
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
    }
  }

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
    setIsPopupVisible(true);
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
    setSelectedImage(null);
  };

  useEffect(() => {
    if (!auth?.isLogged) {
      navigate("/onboarding/eligibility-flow/condition");
    }
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    setIsMobile(isMobile);
  }, [])

  const validateFiles = (values) => {
    values?.documents && setNumberOfDocuments(values?.documents?.length)
  }

  return (
    <div className="d-flex flex-column h-100">
      <h4 className="mb-2 fw-bold">
        Upload profile photo for cannabis cards
      </h4>
      <div className="container px-0 d-flex flex-column justify-content-between">
        <div className="column col-md-12">
          <p className="text-gray-1 mb-3">
            Disclaimer: You will be eligible to receive your medical cannabis
            card upon successfully being issued a medical cannabis prescription
            from treat it.
          </p>
        </div>
        <div className="column col-md-12 col-xl-11">
          <div className="d-flex flex-column flex-lg-row gap-5 gap-lg-3 gap-xl-5 w-100 mt-4 mt-md-5">
            <div className="col-12 col-lg-7 col-xl-8 image-instructions-div rounded-4 d-flex flex-column">
              <div className=" d-flex flex-row justify-content-between gap-0 gap-xl-5 w-100">
                <div className="d-flex flex-column bg-primary-600 text-white img-instruction-header px-5 py-2">
                  <p className="fw-bold mb-0 fs-6">Medical cannabis prescription holder</p>
                  <p className="mb-0">This patient is a UK registered cannabis patient</p>
                </div>
                <div className="d-flex align-items-center justify-content-center">
                  <img
                    src="/images/eligibility/uk-flag.png"
                    className="uk-flag px-5"
                  />
                </div>
              </div>
              <div className="d-flex flex-row justify-content-between px-5 pt-2 w-100 position-relative">
                <div className="d-flex flex-column w-100">
                  <p className="fw-bold text-primary-600 mb-0">ID photo will be added here </p>
                  <div className="d-flex flex-column flex-xl-row gap-0 gap-xl-2">
                    <div className="d-none d-xl-flex flex-column align-items-center">
                      <img
                        src="/images/eligibility/curved-arrow.svg"
                        className="curved-arrow"
                      />
                      <img
                        src="/images/eligibility/profile-circle.png"
                        className="profile-circle"
                      />
                    </div>
                    <div className="d-flex d-xl-none flex-row justify-content-between w-100">
                      <div className="d-flex flex-column align-items-center">
                        <img
                          src="/images/eligibility/curved-arrow.svg"
                          className="curved-arrow"
                        />
                        <img
                          src="/images/eligibility/profile-circle.png"
                          className="profile-circle"
                        />
                      </div>
                      <div className="d-flex d-xl-none flex-column align-items-center justify-content-center border rounded-1 p-1 w-fit h-fit">
                        <img
                          src="/images/eligibility/id-image-qr-code.png"
                          className="id-qr-code"
                        />
                        <p className="scan-text fw-bold mb-0 flow-gap">
                          Scan for
                        </p> <p className="scan-text fw-bold mb-0">prescription
                        </p>
                      </div>
                    </div>
                    <div className="d-flex flex-column flex-lg-row gap-0 gap-lg-4 mt-0 mt-xl-4">
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-column">
                          <p className="mb-1">Given name</p>
                          <p className="fw-bold">{profileData.firstName}</p>
                        </div>
                        <div className="d-flex flex-column">
                          <p className="mb-1">Card number</p>
                          <p className="fw-bold">xxxxx</p>
                        </div>
                      </div>
                      <div className="d-flex flex-column">
                        <div className="d-flex flex-column">
                          <p className="mb-1">Surname</p>
                          <p className="fw-bold">{profileData.lastName}</p>
                        </div>
                        <div className="d-flex flex-column">
                          <p className="mb-1">Expiry date</p>
                          <p className="fw-bold text-nowrap">3 months from prescription</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-none d-xl-flex flex-column position-absolute align-items-center justify-content-center border rounded-1 p-1 h-fit" style={{ right: 22.5, top: '40%', transform: 'translateY(-50%)' }}>
                  <img
                    src="/images/eligibility/id-image-qr-code.png"
                    className="id-qr-code"
                  />
                  <p className="scan-text fw-bold mb-0 flow-gap">
                    Scan for
                  </p> <p className="scan-text fw-bold mb-0">prescription
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-5 col-xl-4 instruction-img py-2 px-2 p-md-2 p-xl-1 rounded d-flex align-items-center justify-content-center">
              <img
                src="/images/profile-guidance-uk.png"
                className="rounded w-100 cursor-pointer img-profile-guidance"
                onClick={() => handleImageClick("/images/profile-guidance-uk.png")}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex mt-3 mt-lg-4 h-100">
        <Form
          initialValues={{}}
          enableReinitialize
          className="d-flex"
          onSubmit={handleSubmit}
        >
          <div className="container px-0 d-flex flex-column mb-12 mb-md-0">
            <div className="column col-md-12 d-flex flex-column w-100 mt-0 mt-md-0 h-100">
              <div className="column col-md-12 col-lg-12 col-xl-11">
                <div className="d-flex flex-column-reverse flex-lg-row align-items-start justify-content-between">
                  <div className="col-12 col-md-12 col-lg-7 col-xl-8">
                    <div className="d-flex flex-row justify-content-between mb-2">
                      <label>ID photo</label>
                      <label className="fw-bold text-primary-600 text-decoration-underline cursor-pointer" onClick={toggleModal}>Digital photo guidelines</label>
                    </div>
                    <DocumentUpload
                      name="documents"
                      multiple={false}
                      fileLimit={1}
                      acceptedTypes={["image/jpeg", "image/png", "image/jpg"]}
                      getFiles={validateFiles}
                    />
                  </div>
                  <div className="col-12 col-lg-5 col-xl-4 d-flex justify-content-center ms-0 ms-lg-3 ms-xl-5 mt-lg-1 mb-4 mb-lg-0">
                    <p className="text-nowrap mb-0 fw-bold cursor-pointer" onClick={() => handleImageClick("/images/profile-guidance-uk.png")}>Click image to Zoom</p></div>
                </div>
              </div>
              <div className="column d-flex flex-row justify-content-center justify-content-md-end align-items-center mt-auto mt-md-8 mt-xl-auto">
                <div className="d-flex flex-column-reverse flex-md-row footer-div px-5 px-md-0 py-4 py-md-0 fixed-single-footer justify-content-md-end">
                  <button
                    className="text-primary-600 bg-transparent pt-2 pb-0 px-4 btn-sm w-md-fit h-fit fw-bold border-0 rounded text-nowrap"
                    onClick={handleNavigate}
                  >
                    <p className="mb-0 fw-bold fs-6">Upload later</p>
                  </button>
                  <button
                    className={
                      `bg-primary-600 text-white py-2 px-5 btn-sm w-md-fit h-fit fw-bold fs-6 border-0 rounded text-nowrap
                      ${(numberOfDocuments === 0) ? "opacity-50" : "opacity-100"}`
                    }
                    type="submit"
                    disabled={numberOfDocuments === 0 || isSubmitting}
                  >
                    {isSubmitting ? <LoadingSpinner loadingText="Continue" /> : "Continue"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
      <IDUploadImagePopup
        show={isPopupVisible}
        onClose={handleClosePopup}
        imageSrc={selectedImage}
      />
      <ImageUploadGuidelinesPopup show={showModal} onClose={toggleModal} />
    </div>
  );
};

export default IdentificationForm;
